<template>
  <v-dialog :model-value="modelValue" :max-width="isSheet ? '100%' : 520" scrollable
    :transition="isSheet ? 'dialog-bottom-transition' : undefined" class="conv-dialog-overlay"
    :class="{ 'conv-dialog-overlay--sheet': isSheet }" @update:model-value="$emit('update:modelValue', $event)">
    <v-card v-if="request" rounded="xl" class="conv-dialog">
      <!-- En-tête -->
      <div class="conv-dialog__header">
        <div class="conv-dialog__icon">
          <v-icon color="#3f51b5" size="22" icon="mdi-forum-outline" />
        </div>
        <div class="conv-dialog__heading">
          <div class="conv-dialog__title">Motif &amp; échanges</div>
          <div class="conv-dialog__sub">{{ subtitle }}</div>
        </div>
        <v-btn icon variant="text" color="#757575" size="small" class="ml-auto" title="Fermer" @click="close">
          <v-icon icon="mdi-close" />
        </v-btn>
      </div>

      <!-- Rappel de la demande -->
      <div class="conv-dialog__recap">
        <v-chip v-if="typeName" size="small" variant="tonal" :color="typeColor" class="conv-dialog__chip">
          {{ typeName }}
        </v-chip>
        <span class="conv-dialog__period">
          <v-icon size="14" icon="mdi-calendar-range" />
          {{ formatDate(request.debut_timestamp) }} → {{ formatDate(request.fin_timestamp) }}
        </span>
        <span v-if="request.nbr_days" class="conv-dialog__days">{{ request.nbr_days }} j</span>
        <v-chip v-if="statut" size="small" variant="tonal" :color="statut.color"
          class="conv-dialog__chip conv-dialog__status">
          <v-icon start size="12" :icon="statut.icon" />
          {{ statut.label }}
        </v-chip>
      </div>

      <!-- Conversation -->
      <v-card-text ref="body" class="conv-dialog__body">
        <conversation-thread :commentaire="request.commentaire" :viewer="viewer" :employee-name="employeeName"
          show-no-reply />
      </v-card-text>

      <!-- Réponse -->
      <div v-if="canReply" class="conv-dialog__footer">
        <conversation-composer ref="composer" v-model="draft" :sending="sending" :placeholder="replyPlaceholder"
          @send="send" />
        <div v-if="error" class="conv-dialog__error">
          <v-icon size="14" icon="mdi-alert-circle-outline" />
          {{ error }}
        </div>

        <!-- Décision de l'administrateur (le message saisi part avec la décision) -->
        <template v-if="decisionMode">
          <div class="conv-dialog__decision">
            <template v-if="decisionMode === 'demande'">
              <v-btn variant="flat" color="#e53935" rounded="lg" style="color:white;" class="conv-dialog__decision-btn"
                :disabled="sending" @click="decide('refuser')">
                <v-icon start size="16" icon="mdi-close-circle-outline" />
                Refuser
              </v-btn>
              <v-btn variant="flat" color="#43a047" rounded="lg" style="color:white;" class="conv-dialog__decision-btn"
                :disabled="sending" @click="decide('accepter')">
                <v-icon start size="16" icon="mdi-check-circle-outline" />
                Accepter
              </v-btn>
            </template>
            <template v-else>
              <v-btn variant="tonal" color="#757575" rounded="lg" class="conv-dialog__decision-btn"
                :disabled="sending" @click="decide('refuser-suppression')">
                <v-icon start size="16" icon="mdi-close-circle-outline" />
                Refuser la suppression
              </v-btn>
              <v-btn variant="flat" color="#9c27b0" rounded="lg" style="color:white;" class="conv-dialog__decision-btn"
                :disabled="sending" @click="decide('confirmer-suppression')">
                <v-icon start size="16" icon="mdi-trash-can-outline" />
                Confirmer la suppression
              </v-btn>
            </template>
          </div>
          <div class="conv-dialog__hint">{{ decisionHint }}</div>
        </template>
      </div>
      <div v-else class="conv-dialog__footer conv-dialog__footer--closed">
        <span class="conv-dialog__closed">
          <v-icon size="15" icon="mdi-lock-outline" />
          {{ closedLabel }}
        </span>
        <v-btn variant="tonal" color="#3f51b5" rounded="lg" @click="close">Fermer</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import ConversationThread from "./ConversationThread.vue";
import ConversationComposer from "./ConversationComposer.vue";
import { getConversation } from "@/core/commentaire";
import { echangeOuvert, envoyerMessage } from "@/core/conversation";

// Même codification que les tableaux de demandes
const STATUTS = {
  0: { label: "En attente", color: "#fb8c00", icon: "mdi-clock-outline" },
  1: { label: "Validée", color: "#43a047", icon: "mdi-check-circle-outline" },
  2: { label: "Refusée", color: "#e53935", icon: "mdi-close-circle-outline" },
  3: { label: "Annulée", color: "#9e9e9e", icon: "mdi-cancel" },
  4: { label: "Suppression demandée", color: "#9c27b0", icon: "mdi-trash-can-outline" },
  5: { label: "Supprimée", color: "#4a148c", icon: "mdi-trash-can" },
};

/**
 * Fenêtre « Motif & échanges » d'une demande de congé, avec réponse possible
 * tant que la demande n'est pas traitée. Sur mobile, s'ouvre depuis le bas de l'écran.
 * Émet « updated » ({ request, commentaire }) après l'envoi d'un message, et
 * « decision » ({ action, request }) quand l'administrateur accepte ou refuse :
 * action = accepter | refuser | confirmer-suppression | refuser-suppression.
 */
export default {
  name: "ConversationDialog",
  components: { ConversationThread, ConversationComposer },
  props: {
    modelValue: { type: Boolean, default: false },
    // Demande de congé (ligne du tableau)
    request: { type: Object, default: null },
    // Qui consulte : « user » (Mon espace) ou « admin » (zone admin)
    viewer: { type: String, default: "user" },
    // Employé à l'origine de la demande
    employeeName: { type: String, default: "" },
    // Personne connectée (signature des messages envoyés)
    authorName: { type: String, default: "" },
    typeName: { type: String, default: "" },
    typeColor: { type: String, default: "#757575" },
    // Affiche les boutons de décision (zone admin)
    decisions: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "updated", "decision"],
  data() {
    return {
      draft: "",
      sending: false,
      error: "",
    };
  },
  computed: {
    isSheet() {
      return !!(this.$vuetify && this.$vuetify.display && this.$vuetify.display.xs);
    },
    statut() {
      if (!this.request) return null;
      return STATUTS[parseInt(this.request.statut)] || null;
    },
    subtitle() {
      const count = this.request ? getConversation(this.request.commentaire).messages.length : 0;
      const who = this.viewer === "admin" ? this.employeeName || "Demande de congé" : "Votre demande de congé";
      if (!count) return who;
      return `${who} · ${count} message${count > 1 ? "s" : ""}`;
    },
    canReply() {
      return echangeOuvert(this.request);
    },
    replyPlaceholder() {
      if (this.viewer === "admin") {
        const firstName = (this.employeeName || "").split(" ")[0];
        return firstName ? `Répondre à ${firstName}…` : "Répondre à l'employé…";
      }
      return "Répondre à l'administrateur…";
    },
    // « demande » (statut 0), « suppression » (statut 4) ou null
    decisionMode() {
      if (!this.decisions || !this.canReply) return null;
      return parseInt(this.request.statut) === 4 ? "suppression" : "demande";
    },
    decisionHint() {
      const hasDraft = !!this.draft.trim();
      if (this.decisionMode === "suppression") {
        return hasDraft
          ? "Votre motif sera ajouté à la conversation avec la décision."
          : "Motif de décision obligatoire : écrivez-le ci-dessus avant de choisir.";
      }
      return hasDraft
        ? "Le message saisi sera envoyé avec la décision."
        : "Vous pouvez répondre, ou décider directement.";
    },
    closedLabel() {
      if (!this.request) return "";
      return parseInt(this.request.statut) === 3
        ? "Demande annulée : l'échange est clos"
        : "Demande traitée : l'échange est clos";
    },
  },
  watch: {
    draft() {
      if (this.error) this.error = "";
    },
    modelValue(open) {
      if (open) this.scrollToEnd();
    },
    request(newRequest, oldRequest) {
      const sameRequest = newRequest && oldRequest && newRequest.id_demande === oldRequest.id_demande;
      if (!sameRequest) {
        this.draft = "";
        this.error = "";
      }
    },
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },
    send(texte) {
      this.sendText(texte);
    },
    // Envoie un message ; onSuccess n'est appelé que si le message est enregistré
    sendText(texte, onSuccess) {
      if (!this.request || this.sending) return;
      const request = this.request;
      this.sending = true;
      this.error = "";
      envoyerMessage({ request, role: this.viewer, nom: this.authorName, texte }, (res) => {
        this.sending = false;
        if (!res.ok) {
          this.error = res.message;
          return;
        }
        this.draft = "";
        this.$emit("updated", { request, commentaire: res.commentaire });
        this.scrollToEnd();
        if (onSuccess) onSuccess();
      });
    },
    // Décision de l'administrateur : le message saisi est d'abord ajouté à la conversation
    decide(action) {
      if (!this.request || this.sending) return;
      const request = this.request;
      const texte = this.draft.trim();
      const suppression = action === "confirmer-suppression" || action === "refuser-suppression";
      if (suppression && !texte) {
        this.error = "Précisez le motif de votre décision avant de valider.";
        if (this.$refs.composer) this.$refs.composer.focus();
        return;
      }
      const emitDecision = () => this.$emit("decision", { action, request });
      if (!texte) {
        emitDecision();
        return;
      }
      const prefixe = { "confirmer-suppression": "Suppression confirmée : ", "refuser-suppression": "Suppression refusée : " };
      this.sendText((prefixe[action] || "") + texte, emitDecision);
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const body = this.$refs.body && this.$refs.body.$el;
        if (body) body.scrollTop = body.scrollHeight;
      });
    },
    formatDate(ts) {
      if (!ts) return "—";
      return new Date(parseInt(ts)).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.conv-dialog__header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 20px 14px;
}

.conv-dialog__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #e8eaf6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.conv-dialog__heading {
  min-width: 0;
}

.conv-dialog__title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
}

.conv-dialog__sub {
  margin-top: 2px;
  font-size: 12px;
  color: #9e9e9e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conv-dialog__recap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin: 0 20px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
}

.conv-dialog__chip {
  font-weight: 700;
}

.conv-dialog__period {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: #424242;
  white-space: nowrap;
}

.conv-dialog__days {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
}

.conv-dialog__status {
  margin-left: auto;
}

.conv-dialog__body {
  padding: 20px !important;
}

.conv-dialog__footer {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 20px 18px;
  border-top: 1px solid #f0f0f0;
}

.conv-dialog__footer--closed {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.conv-dialog__closed {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9e9e9e;
}

.conv-dialog__decision {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

/* Côte à côte si la place le permet, l'un sous l'autre sinon (jamais de libellé tronqué) */
.conv-dialog__decision-btn {
  flex: 1 1 180px;
  min-width: max-content;
}

.conv-dialog__hint {
  padding: 0 6px;
  font-size: 11.5px;
  color: #9e9e9e;
}

.conv-dialog__error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
  font-size: 12px;
  color: #e53935;
}

@media (max-width: 600px) {
  .conv-dialog__header {
    padding: 18px 16px 12px;
  }

  .conv-dialog__recap {
    margin: 0 16px;
  }

  .conv-dialog__status {
    margin-left: 0;
  }

  .conv-dialog__body {
    padding: 18px 16px !important;
  }

  .conv-dialog__footer {
    padding: 10px 12px 14px;
  }
}
</style>

<style>
/* Panneau « bottom sheet » sur mobile (overlay téléporté : styles non scopés) */
.v-dialog.conv-dialog-overlay--sheet {
  align-items: flex-end;
}

.v-dialog.conv-dialog-overlay--sheet > .v-overlay__content {
  width: 100%;
  max-height: 90%;
  margin: 0;
}

.v-dialog.conv-dialog-overlay--sheet > .v-overlay__content > .v-card {
  border-radius: 20px 20px 0 0 !important;
}

.v-dialog.conv-dialog-overlay--sheet .conv-dialog__footer {
  padding-bottom: calc(14px + env(safe-area-inset-bottom));
}
</style>
